import {ApiError, NetworkError, useFetcher} from 'thermia-react-container';
import {config} from '../config';
import {Device} from '../types/device';
import {OnsiteConfig} from '../types/onsiteConfig';
import {
  CustomerUpdate,
  DeviceUpdate,
  HeatingSetupUpdate,
  LocationUpdate,
  PartnerInfo,
  Registration
} from '../types/registrationTypes';
import {RegistrationConflictError} from "../validation/Errors";
import {useIsAdmin} from "../hooks/useIsAdmin";
import {OnBehalfOfService} from "./onBehalfOfService";

const CONFIG_VERSION_HEADER = 'web/3.0.0';

const useRegistrationService = (admin: boolean = false) => {
  const { fetcher } = useFetcher();
  const isAdmin = useIsAdmin();
  const API_BASE_URL = config.apiUrl + "/registration-service";
  const getApiUrl = () => API_BASE_URL + ((admin || (isAdmin && OnBehalfOfService.partner)) ? "/admin" : "");
  const headers = { 'onsite-version': CONFIG_VERSION_HEADER };
  
  const getRegistrationsConfig = async (): Promise<OnsiteConfig> => {
    const url = `${API_BASE_URL}/config`;
    return await fetcher<OnsiteConfig>('GET', url, headers);
  };

  const postRegistrationPart = async (registrationId: string, part: string, body: any) => {
    try {
      const url = `${getApiUrl()}/registrations/${registrationId}/${part}`;
      return await fetcher<Registration>('POST', url, headers, body);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new RegistrationConflictError(error.response, error.message);
      }
      throw error;
    }
  };

  const postCustomer = async (registrationId: string, customerUpdate: CustomerUpdate) => {
    return postRegistrationPart(registrationId, 'customer', customerUpdate);
  };

  const postLocation = async (registrationId: string, locationUpdate: LocationUpdate) => {
    return postRegistrationPart(registrationId, 'location', locationUpdate);
  };

  const postSystemDescription = async (registrationId: string, systemDescriptionUpdate: HeatingSetupUpdate) => {
    return postRegistrationPart(registrationId, 'system-description', systemDescriptionUpdate);
  };

  const postDevices = async (registrationId: string, deviceUpdate: DeviceUpdate) => {
    return postRegistrationPart(registrationId, 'devices', deviceUpdate);
  };

  const getCompletedRegistrationsMetaData = async (): Promise<Registration[]> => {
    try {
      const url = `${getApiUrl()}/registrations/completed/${OnBehalfOfService.partner?.id ?? ''}`;

      const response = await fetcher<Registration[]>('GET', url, headers);
      if (!Array.isArray(response)) {
        return [];
      }

      return response.flatMap((reg) => {
        try {
          return reg as Registration;
        } catch (e) {
          return [];
        }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error while fetching complete registration: ' + error.message);
      }
      throw error;
    }
  };

  const reopenCompletedRegistration = async (registrationId: string) => {
    try {
      const url = `${getApiUrl()}/registrations/${registrationId}/unlock`;
      return await fetcher<Registration>('POST', url, headers);
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error while reopen completed registration: ' + error.message);
      }
      throw error;
    }
  };

  const getRegistrations = async (): Promise<Registration[]> => {
    try {
      const url = `${getApiUrl()}/registrations/ongoing/${OnBehalfOfService.partner?.id ?? ''}`;

      const response = await fetcher<Registration[]>('GET', url, headers);
      if (!Array.isArray(response)) {
        console.log(null, '[GET] /registrations - returned non-array value');
        return [];
      }

      return response.flatMap((reg) => {
        try {
          return reg as Registration;
        } catch (e) {
          console.log(e, '[GET] /registrations - Unable to convert reg to registrationClass id: ' + reg.id);
          return [];
        }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error while fetching registrations: ' + error.message);
      }
      throw error;
    }
  };

  const getRegistration = async (registrationId: Registration['id']): Promise<Registration> => {
    try {
      const url = `${getApiUrl()}/registrations/${registrationId}`;
      return await fetcher<Registration>('GET', url, headers);
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error while fetching registration: ' + error.message);
      }
      throw error;
    }
  };

  const postRegistration = async (registration: Registration): Promise<Registration> => {
    try {
      const url = `${getApiUrl()}/registrations/${OnBehalfOfService.partner?.id ?? ''}`;
      return await fetcher<Registration>('POST', url, headers, registration);
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error while update registration: ' + error.message);
      }
      throw error;
    }
  };

  const deleteRegistration = async (id: Registration['id']): Promise<void> => {
    try {
      const url = `${getApiUrl()}/registrations/${id}`;
      return await fetcher('DELETE', url, headers);
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'Error when try to delete registration: ' + error.message);
      }
      throw error;
    }
  };

  const postCompleteRegistration = async (registration: Registration): Promise<void> => {
    try {
      const url = `${getApiUrl()}/registrations/${registration.id}/complete`;
      const body = null;
      return await fetcher('POST', url, headers, body);
    } catch (error) {
      if (error instanceof ApiError) {
        console.log('Error when try to complete registration: ' + error.message);
      }
      throw error;
    }
  };

  const getUnregisteredDevices = async () => {
    try {
      const url = `${getApiUrl()}/unregistered-devices/${OnBehalfOfService.partner?.id ?? ''}`;
      const response = await fetcher<Device[]>('GET', url, headers);
      if (!response) {
        return [];
      }
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        console.log(error, 'error while fetching unregistered devices: ' + error.message);
      }
      if (error instanceof NetworkError) {
        console.log(error, 'Unable to fetch due to network error: ' + error.message);
      }
      throw error;
    }
  };

  const fetchAdminRegistrations = async (searchTerm: string) => {
    searchTerm = encodeURIComponent(searchTerm.trim());
    const url = `${getApiUrl()}/registration/${searchTerm}`;
    return await fetcher<Registration[]>('GET', url, headers);
  };

  const getPartners = async (searchTerm: string) => {
    searchTerm = encodeURIComponent(searchTerm.trim());
    const url = `${getApiUrl()}/partners/${searchTerm}`;
    return await fetcher<PartnerInfo[]>('GET', url, headers);
  };

  const getPartner = async (id: string) => {
    const url = `${getApiUrl()}/partner/${id}`;
    return await fetcher<PartnerInfo>('GET', url, headers);
  };

  return {
    fetchAdminRegistrations,
    getRegistrationsConfig,
    getCompletedRegistrationsMetaData,
    getUnregisteredDevices,
    getRegistrations,
    getRegistration,
    postCustomer,
    postLocation,
    postSystemDescription,
    postDevices,
    deleteRegistration,
    postCompleteRegistration,
    reopenCompletedRegistration,
    postRegistration,
    getPartners,
    getPartner,
  };
};

export { useRegistrationService };

